import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Article as ArticleIcon } from "@mui/icons-material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import LoadSpinner from "../../../utilities/LoadSpinner";

export const PDF = (props) => {
  const { block, editor } = props;
  const { url, name } = block.props;
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate it's a PDF
    if (file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }

    setIsUploading(true);

    try {
      // Call the uploadFile function passed from SectionInputsModal2
      const fileUrl = await props.uploadFile(file);

      // Update the block with the file URL and name
      editor.updateBlock(block, {
        type: "pdf",
        props: {
          url: fileUrl,
          name: file.name,
        },
      });
    } catch (error) {
      console.error("Error uploading PDF:", error);
      toast.error("Failed to upload PDF. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // STATE 1: Uploading - Show loading state (check this first!)
  if (isUploading) {
    return (
      <div className="w-full border border-gray-200 rounded-lg bg-white outline-none focus:outline-none" contentEditable={false}>
        <div className="flex flex-col items-center justify-center gap-y-4 py-12 px-6">
          <LoadSpinner />
          <p className="text-gray-600 text-sm">Uploading PDF...</p>
        </div>
      </div>
    );
  }

  // STATE 2: No file uploaded - Show upload button
  if (!url) {
    return (
      <div
        className="my-3 w-full p-4 border-2 border-dashed border-violet-300 rounded-xl text-sm text-violet-700 hover:bg-violet-50 hover:border-violet-500 transition-colors flex items-center justify-center gap-2 cursor-pointer outline-none focus:outline-none"
        onClick={() => fileInputRef.current?.click()}
        contentEditable={false}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="application/pdf"
          style={{ display: "none" }}
        />
        <CloudUploadIcon style={{ fontSize: 18 }} />
        <span>Upload PDF Document</span>
      </div>
    );
  }

  // STATE 3: File uploaded - Show PDF embed with controls
  // Detect if we're on a mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div className="w-full border border-gray-200 rounded-lg overflow-hidden bg-white outline-none focus:outline-none" contentEditable={false}>
      {/* Header with file name and controls */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <ArticleIcon className="text-violet-600" style={{ fontSize: 20 }} />
          <span className="text-sm font-medium text-gray-700">{name}</span>
        </div>
        {!isMobile && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 font-medium transition-colors"
          >
            Open in new tab
            <ArticleIcon style={{ fontSize: 16 }} />
          </a>
        )}
      </div>

      {/* PDF Content */}
      {isMobile ? (
        // Mobile: Direct link to open in native PDF viewer
        <div className="px-4 py-12 text-center">
          <a
            href={url}
            className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors mb-4"
          >
            <ArticleIcon style={{ fontSize: 20 }} />
            Open PDF
          </a>
          <p className="text-sm text-gray-500">Opens in your device's PDF viewer</p>
        </div>
      ) : (
        // Desktop: Use iframe (more reliable than embed)
        <iframe
          src={url}
          className="w-full"
          style={{ height: "600px", border: "none" }}
          title={name}
        />
      )}
    </div>
  );
};
